import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Mail, Server, Palette, Send, RefreshCw, Check, X, 
  AlertCircle, Clock, Loader2, Eye, EyeOff, FileText, Zap, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('cashx_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

export default function AdminEmail() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  
  // Form states
  const [enabled, setEnabled] = useState(false);
  const [smtp, setSmtp] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    use_ssl: false,
    timeout: 30
  });
  const [branding, setBranding] = useState({
    sender_name: 'Cash-X',
    sender_email: '',
    reply_to: '',
    logo_url: '',
    primary_color: '#2F80FF',
    footer_text: 'Cash-X GmbH | Ihre moderne Kassenlösung',
    footer_address: ''
  });
  
  // Test dialog
  const [testDialog, setTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  
  // Password visibility
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchLogs();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email/settings`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.configured && data.settings) {
          setEnabled(data.enabled);
          if (data.settings.smtp) {
            setSmtp({
              ...smtp,
              ...data.settings.smtp,
              password: '' // Don't show masked password
            });
          }
          if (data.settings.branding) {
            setBranding({ ...branding, ...data.settings.branding });
          }
          setSettings(data);
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/email/logs?limit=20`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSave = async () => {
    // Validierung
    if (!smtp.host || !smtp.username || !branding.sender_email) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/email/settings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          smtp: {
            ...smtp,
            // Nur senden wenn geändert
            password: smtp.password || undefined
          },
          branding,
          enabled
        })
      });
      
      if (!response.ok) throw new Error('Failed to save');
      
      toast.success('E-Mail-Einstellungen gespeichert');
      fetchSettings();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }
    
    setTestSending(true);
    try {
      const response = await fetch(`${API_URL}/api/email/test`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          recipient: testEmail,
          subject: 'Cash-X Test-E-Mail',
          message: 'Dies ist eine Test-E-Mail von Cash-X.'
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Fehler beim Senden');
      }
      
      toast.success('Test-E-Mail gesendet!', {
        description: `An: ${testEmail}`
      });
      setTestDialog(false);
      setTestEmail('');
      fetchLogs();
    } catch (error) {
      toast.error('Fehler beim Senden', {
        description: error.message
      });
    } finally {
      setTestSending(false);
    }
  };

  const handleRetry = async (logId) => {
    try {
      const response = await fetch(`${API_URL}/api/email/retry/${logId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('E-Mail wird erneut gesendet');
        setTimeout(fetchLogs, 2000);
      }
    } catch (error) {
      toast.error('Fehler beim Retry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      sent: { label: 'Gesendet', color: 'bg-emerald-500/20 text-emerald-400', icon: Check },
      failed: { label: 'Fehlgeschlagen', color: 'bg-red-500/20 text-red-400', icon: X },
      pending: { label: 'Wartend', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
      retrying: { label: 'Retry...', color: 'bg-blue-500/20 text-blue-400', icon: RefreshCw }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} border-0`}>
        <Icon className="h-3 w-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <Skeleton className="h-8 w-48 mb-4 bg-slate-700" />
        <Skeleton className="h-96 w-full bg-slate-700 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 
            className="text-2xl md:text-3xl font-bold flex items-center gap-3"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="admin-email-title"
          >
            <Mail className="h-8 w-8 text-[#2F80FF]" />
            E-Mail-Einstellungen
          </h1>
          <p className="text-slate-400 mt-1">SMTP-Konfiguration und Versand-Logs</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="email-enabled-switch"
            />
            <span className="text-sm text-slate-400">
              {enabled ? 'Aktiviert' : 'Deaktiviert'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* SMTP Settings */}
          <Card 
            className="p-6" 
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
          >
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Server className="h-5 w-5 text-slate-400" />
              SMTP-Server
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smtp-host">Host *</Label>
                <Input
                  id="smtp-host"
                  placeholder="smtp.example.com"
                  value={smtp.host}
                  onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                  data-testid="smtp-host-input"
                />
              </div>
              <div>
                <Label htmlFor="smtp-port">Port</Label>
                <Input
                  id="smtp-port"
                  type="number"
                  placeholder="587"
                  value={smtp.port}
                  onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                  data-testid="smtp-port-input"
                />
              </div>
              <div>
                <Label htmlFor="smtp-user">Benutzername *</Label>
                <Input
                  id="smtp-user"
                  placeholder="user@example.com"
                  value={smtp.username}
                  onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                  data-testid="smtp-username-input"
                />
              </div>
              <div>
                <Label htmlFor="smtp-password">Passwort *</Label>
                <div className="relative mt-1">
                  <Input
                    id="smtp-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={settings?.configured ? '••••••••' : 'Passwort'}
                    value={smtp.password}
                    onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
                    className="bg-[#0E1628] border-slate-700 pr-10"
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={smtp.use_tls}
                  onCheckedChange={(v) => setSmtp({ ...smtp, use_tls: v, use_ssl: v ? false : smtp.use_ssl })}
                />
                <span className="text-sm">TLS (Port 587)</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={smtp.use_ssl}
                  onCheckedChange={(v) => setSmtp({ ...smtp, use_ssl: v, use_tls: v ? false : smtp.use_tls })}
                />
                <span className="text-sm">SSL (Port 465)</span>
              </div>
            </div>
          </Card>

          {/* Branding Settings */}
          <Card 
            className="p-6" 
            style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
          >
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5 text-slate-400" />
              Branding
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sender-name">Absendername</Label>
                <Input
                  id="sender-name"
                  placeholder="Cash-X"
                  value={branding.sender_name}
                  onChange={(e) => setBranding({ ...branding, sender_name: e.target.value })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                  data-testid="sender-name-input"
                />
              </div>
              <div>
                <Label htmlFor="sender-email">Absender E-Mail *</Label>
                <Input
                  id="sender-email"
                  type="email"
                  placeholder="noreply@example.com"
                  value={branding.sender_email}
                  onChange={(e) => setBranding({ ...branding, sender_email: e.target.value })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                  data-testid="sender-email-input"
                />
              </div>
              <div>
                <Label htmlFor="reply-to">Reply-To (optional)</Label>
                <Input
                  id="reply-to"
                  type="email"
                  placeholder="support@example.com"
                  value={branding.reply_to}
                  onChange={(e) => setBranding({ ...branding, reply_to: e.target.value })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                />
              </div>
              <div>
                <Label htmlFor="logo-url">Logo URL (optional)</Label>
                <Input
                  id="logo-url"
                  placeholder="https://example.com/logo.png"
                  value={branding.logo_url}
                  onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                />
              </div>
              <div>
                <Label htmlFor="primary-color">Primärfarbe</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="primary-color"
                    type="color"
                    value={branding.primary_color}
                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                    className="w-12 h-10 p-1 bg-[#0E1628] border-slate-700"
                  />
                  <Input
                    value={branding.primary_color}
                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                    className="flex-1 bg-[#0E1628] border-slate-700"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-4">
              <Label htmlFor="footer-text">Footer Text</Label>
              <Textarea
                id="footer-text"
                placeholder="Ihr Unternehmen | Kontakt"
                value={branding.footer_text}
                onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })}
                className="mt-1 bg-[#0E1628] border-slate-700"
                rows={2}
              />
            </div>
            <div className="mt-4">
              <Label htmlFor="footer-address">Footer Adresse (optional)</Label>
              <Input
                id="footer-address"
                placeholder="Musterstraße 1, 12345 Berlin"
                value={branding.footer_address}
                onChange={(e) => setBranding({ ...branding, footer_address: e.target.value })}
                className="mt-1 bg-[#0E1628] border-slate-700"
              />
            </div>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-email-settings-btn"
              className="bg-[#2F80FF] hover:bg-[#4A92FF] text-white"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Einstellungen speichern
            </Button>
            <Button
              variant="outline"
              onClick={() => setTestDialog(true)}
              disabled={!settings?.configured}
              data-testid="test-email-btn"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <Send className="h-4 w-4 mr-2" />
              Testmail senden
            </Button>
          </div>
        </div>

        {/* Logs Column */}
        <Card 
          className="p-6 h-fit" 
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-400" />
              Versand-Log
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchLogs}
              disabled={logsLoading}
              className="text-slate-400 hover:text-white"
            >
              {logsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>

          {logsLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full bg-slate-700 rounded" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Keine E-Mails versendet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {logs.map((log) => (
                <div 
                  key={log.log_id}
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: '#0E1628' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{log.recipient}</p>
                      <p className="text-sm text-slate-400 truncate">{log.subject}</p>
                      <p className="text-xs text-slate-500 mt-1">{formatDate(log.created_at)}</p>
                    </div>
                    {getStatusBadge(log.status)}
                  </div>
                  {log.status === 'failed' && (
                    <div className="mt-2">
                      <p className="text-xs text-red-400 mb-2">{log.message}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRetry(log.log_id)}
                        className="text-xs text-[#2F80FF] hover:text-[#4A92FF] p-0 h-auto"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Erneut senden
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Test Email Dialog */}
      <Dialog open={testDialog} onOpenChange={setTestDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-[#2F80FF]" />
              Test-E-Mail senden
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Senden Sie eine Test-E-Mail um die Konfiguration zu prüfen.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="test-recipient">Empfänger</Label>
            <Input
              id="test-recipient"
              type="email"
              placeholder="test@example.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="mt-1 bg-[#0E1628] border-slate-700"
              data-testid="test-email-recipient"
            />
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTestDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleTestEmail}
              disabled={testSending || !testEmail}
              className="bg-[#2F80FF] hover:bg-[#4A92FF] text-white"
              data-testid="send-test-email-btn"
            >
              {testSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
